import { SelectChangeEvent } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { AnalyticsEvents } from '../../../enums/AnalyticsEvents';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { useIsFeatureEnabled } from '../../../hooks/useIsFeatureEnabled';
import { useTranslation } from '../../../hooks/useTranslation';
import { getEnabledCountries } from '../../../interfaces/Country';
import { FeatureGate } from '../../../interfaces/FeatureGate';
import { hasPartnerSession } from '../../../services/partnerSession';
import StyledFormItem from '../../../styles/StyledFormItem';
import Dropdown from '../../shared/DropDown/DropDown';
import TelephoneInput from '../../shared/TelephoneInput/TelephoneInput';
import Text from '../../shared/Text/Text';
import WorkflowStepLabel from '../../shared/WorkflowStepLabel/WorkflowStepLabel';
import StyledCountryCodeLabel from './StyledCountryCodeLabel';
import StyledFlag from './StyledFlag';

interface AgentLineStepProps {
  description?: string;
  label?: string;
  stepLabel?: string;
}

const AgentLineStep: React.FC<AgentLineStepProps> = ({
  description,
  label,
  stepLabel,
}) => {
  const { t } = useTranslation();
  const isPartnerIframe = hasPartnerSession();
  const hasUSFISupport = useIsFeatureEnabled(
    FeatureGate.US_FI_PHONE_NUMBERS_SUPPORT
  );
  const enabledCountries = getEnabledCountries(hasUSFISupport);

  const listOfSupportedCountries = Object.keys(enabledCountries).map(
    country => ({
      label: (
        <StyledCountryCodeLabel>
          <StyledFlag
            src={enabledCountries[country].flag}
            alt={t("a country's flag")}
          />
          <span>
            {enabledCountries[country].name} ({enabledCountries[country].prefix}
            )
          </span>
        </StyledCountryCodeLabel>
      ),
      value: enabledCountries[country].code,
    })
  );

  const {
    formState: { errors },
    setValue,
  } = useFormContext();

  const { track } = useAnalytics();

  const handleCountryCodeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setValue('system_phone_number_country_code', value);
    track(AnalyticsEvents.AGENT_LINE_COUNTRY_CODE_SELECTED, {
      countryCode: value,
    });
  };

  return (
    <div>
      <WorkflowStepLabel
        stepLabel={stepLabel}
        label={label ?? t('Agent Line')}
        description={description}
      />

      {isPartnerIframe ? (
        <>
          <h4>
            {t('MixVoIP will assign a SIP address or extension to this agent.')}
          </h4>
          <p>
            {t(
              'A phone number is not required here — routing is handled from the cockpit. You can still add a failure backup number below.'
            )}
          </p>
        </>
      ) : (
        <>
          <h4>
            {t('We will now create a')} <em>{t('new phone number')}</em>{' '}
            {t('for your new agent')}
          </h4>

          <p>
            {t(
              'If you want to use your existing phone number, you will still be able to forward your calls to your agent phone number after having created and assigned a new phone number for your agent.'
            )}
          </p>

          <StyledFormItem>
            <Text type="label">
              {t('Choose the country code prefix of your new phone number')}
            </Text>
            <Dropdown
              name="system_phone_number_country_code"
              required={true}
              options={listOfSupportedCountries}
              fullWidth
              withError={!!errors.system_phone_number_country_code}
              helperText={
                !!errors.system_phone_number_country_code
                  ? t('Country code is required')
                  : ''
              }
              onChange={handleCountryCodeChange}
            />
          </StyledFormItem>
        </>
      )}

      <StyledFormItem>
        <TelephoneInput
          name="failure_backup_phone_number"
          labelText={t('Failure Backup Phone Number')}
          marginBottom="5px"
        />
        <small className="info-text">
          {t(
            'If your agent repeatedly fails to handle a call, we will transfer it to this number instead (only during working hours).'
          )}
        </small>
      </StyledFormItem>
    </div>
  );
};

export default AgentLineStep;
